import React, { useCallback, useEffect, useState } from 'react';
import { apiUrl } from '../utils/apiBase';
import { authFetch } from '../utils/auth-fetch';
import { RatingChip } from './rating-chip';
import {
  initialsOf,
  outgoingOfferLabel,
  partnerBlockMessage,
  shouldRenderPool,
  type MyPartnerState,
  type PartnerSearchItem,
} from './partner-search';

interface Props {
  leagueId: string;
  roundId: string;
  accessCode: string | null;
  isDoubles: boolean;
  isLoggedIn: boolean;
  // A formed pair becomes an ordinary signup, so the page around this panel has to
  // reload its participant list and signup state.
  onPairFormed: () => void;
}

function Monogram({ name }: { name: string }): JSX.Element {
  return (
    <span className="round-signup-panel__monogram" aria-hidden="true">
      {initialsOf(name)}
    </span>
  );
}

export const PartnerSearchPanel: React.FC<Props> = ({
  leagueId,
  roundId,
  accessCode,
  isDoubles,
  isLoggedIn,
  onPairFormed,
}) => {
  const [items, setItems] = useState<PartnerSearchItem[]>([]);
  const [enabled, setEnabled] = useState(false);
  const [me, setMe] = useState<MyPartnerState | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const mePath = `/api/me/leagues/${leagueId}/rounds/${roundId}`;
  const codeQuery = accessCode ? `?code=${encodeURIComponent(accessCode)}` : '';

  const loadPool = useCallback(async () => {
    try {
      const res = await fetch(apiUrl(`/api/public/leagues/${leagueId}/rounds/${roundId}/partner-search`));
      if (!res.ok) {
        return;
      }

      const data = (await res.json()) as { enabled: boolean; items: PartnerSearchItem[] };
      setItems(data.items ?? []);
      setEnabled(!!data.enabled);
    } catch {
      // A pool that fails to load simply does not render — it is never the reason
      // somebody cannot see the Kolejka.
    }
  }, [leagueId, roundId]);

  const loadMine = useCallback(async () => {
    if (!isLoggedIn) {
      setMe(null);

      return;
    }

    try {
      const res = await authFetch(`/api/me/leagues/${leagueId}/rounds/${roundId}/partner-search${codeQuery}`);
      setMe(res.ok ? ((await res.json()) as MyPartnerState) : null);
    } catch {
      setMe(null);
    }
  }, [isLoggedIn, leagueId, roundId, codeQuery]);

  useEffect(() => {
    loadPool();
  }, [loadPool]);

  useEffect(() => {
    loadMine();
  }, [loadMine]);

  async function run(
    path: string,
    init: RequestInit,
    okMessage: string,
    afterPairing = false,
  ): Promise<void> {
    setBusy(true);
    setError(null);
    setMessage(null);

    try {
      const res = await authFetch(path, {
        ...init,
        headers: { 'Content-Type': 'application/json', ...(init.headers as Record<string, string>) },
      });

      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setError(partnerBlockMessage(body.error ?? ''));

        return;
      }

      setMessage(okMessage);
      await Promise.all([loadPool(), loadMine()]);

      if (afterPairing) {
        onPairFormed();
      }
    } catch {
      setError('Błąd sieci.');
    } finally {
      setBusy(false);
    }
  }

  const mySearch = me?.mySearch && me.mySearch.status === 'OPEN' ? me.mySearch : null;
  const myPlayerId = me?.player?.id ?? null;
  const others = items.filter((item) => item.playerId !== myPlayerId);
  const offerByPlayer = new Map((me?.myOffers ?? []).map((offer) => [offer.toPlayerId, offer]));

  if (!shouldRenderPool({ isDoubles, enabled, itemCount: items.length })) {
    return null;
  }

  return (
    <section className="partner-pool" data-testid="partner-search-panel">
      <div className="partner-pool__head">
        <h2 className="partner-pool__title">Szukają partnera</h2>
        <span className="partner-pool__count" data-testid="partner-search-count">{others.length}</span>
      </div>
      <p className="partner-pool__hint">
        Nie masz z kim zagrać debla? Zgłoś się tutaj. Nikt nie zapisze Cię automatycznie —
        propozycje spływają do Ciebie, a parę zawiązujesz dopiero swoją akceptacją.
      </p>

      {error && <p className="auth-error" data-testid="partner-search-error">{error}</p>}
      {message && <p className="auth-message" data-testid="partner-search-message">{message}</p>}

      {!isLoggedIn && (
        <p className="partner-pool__gate" data-testid="partner-search-login-gate">
          Żeby zgłosić się lub odpowiedzieć komuś, musisz być zalogowany.
        </p>
      )}

      {isLoggedIn && me && !mySearch && !me.canUsePool && (
        <p className="partner-pool__gate" data-testid="partner-search-block">
          {partnerBlockMessage(me.poolBlock)}
        </p>
      )}

      {isLoggedIn && me?.canUsePool && !mySearch && (
        <form
          className="partner-pool__form"
          data-testid="partner-search-form"
          onSubmit={(e) => {
            e.preventDefault();
            run(
              `${mePath}/partner-search`,
              { method: 'POST', body: JSON.stringify({ note: note.trim() || undefined, code: accessCode ?? undefined }) },
              'Jesteś w puli — inni zawodnicy mogą teraz wysłać Ci propozycję.',
            );
          }}
        >
          <label className="partner-pool__label" htmlFor="partner-note">
            Notatka dla szukających (opcjonalnie)
          </label>
          <input
            id="partner-note"
            type="text"
            maxLength={200}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="np. gram lewą, wolę defensywę"
            data-testid="partner-search-note"
            aria-describedby="partner-note-hint"
          />
          <p className="partner-pool__hint" id="partner-note-hint">
            Notatkę widzi każdy, kto otworzy stronę tej kolejki — nie wpisuj tu numeru
            telefonu ani adresu. Kasujemy ją po rozliczeniu kolejki.
          </p>
          <button type="submit" className="btn-primary" disabled={busy} data-testid="partner-search-submit">
            {busy ? 'Zgłaszam…' : 'Szukam partnera'}
          </button>
        </form>
      )}

      {mySearch && (
        <div className="partner-pool__mine" data-testid="partner-search-mine">
          <div className="partner-pool__mine-head">
            <h3 className="partner-pool__mine-title">Twoje zgłoszenie jest aktywne</h3>
            <button
              type="button"
              className="btn-secondary"
              disabled={busy}
              data-testid="partner-search-cancel"
              onClick={() =>
                run(
                  `${mePath}/partner-search${codeQuery}`,
                  { method: 'DELETE' },
                  'Zgłoszenie wycofane.',
                )
              }
            >
              Wycofaj zgłoszenie
            </button>
          </div>
          {mySearch.note && <p className="partner-pool__note">„{mySearch.note}"</p>}

          <h4 className="partner-pool__subtitle">
            Propozycje dla Ciebie
            <span className="partner-pool__count" data-testid="partner-offers-count">
              {mySearch.offers.filter((offer) => offer.status === 'PENDING').length}
            </span>
          </h4>

          {mySearch.offers.filter((offer) => offer.status === 'PENDING').length === 0 ? (
            <p className="partner-pool__empty">Nikt jeszcze się nie odezwał. Twoje zgłoszenie widnieje na liście poniżej.</p>
          ) : (
            <ul className="partner-pool__list" data-testid="partner-offers-list">
              {mySearch.offers
                .filter((offer) => offer.status === 'PENDING')
                .map((offer) => (
                  <li key={offer.id} className="partner-pool__row partner-pool__row--offer">
                    <Monogram name={offer.fromPlayerName} />
                    <span className="partner-pool__name">
                      {offer.fromPlayerName}
                      {offer.message && <span className="partner-pool__note-inline">„{offer.message}"</span>}
                    </span>
                    <RatingChip tag="D" value={offer.doublesRating} />
                    <span className="partner-pool__actions">
                      <button
                        type="button"
                        className="btn-primary"
                        disabled={busy}
                        data-testid={`partner-offer-accept-${offer.id}`}
                        onClick={() =>
                          run(
                            `${mePath}/partner-offers/${offer.id}/accept`,
                            { method: 'POST', body: JSON.stringify({ code: accessCode ?? undefined }) },
                            `Gracie razem — para z ${offer.fromPlayerName} jest zapisana na kolejkę.`,
                            true,
                          )
                        }
                      >
                        Zagram z nim
                      </button>
                      <button
                        type="button"
                        className="btn-secondary"
                        disabled={busy}
                        data-testid={`partner-offer-decline-${offer.id}`}
                        onClick={() =>
                          run(
                            `${mePath}/partner-offers/${offer.id}/decline${codeQuery}`,
                            { method: 'POST' },
                            'Propozycja odrzucona.',
                          )
                        }
                      >
                        Nie teraz
                      </button>
                    </span>
                  </li>
                ))}
            </ul>
          )}
        </div>
      )}

      {others.length === 0 ? (
        <p className="partner-pool__empty" data-testid="partner-search-empty">
          Nikt jeszcze nie szuka partnera na tę kolejkę.
        </p>
      ) : (
        <ul className="partner-pool__list" data-testid="partner-search-list">
          {others.map((item) => {
            const sentOffer = offerByPlayer.get(item.playerId);

            return (
              <li key={item.id} className="partner-pool__row">
                <Monogram name={item.playerName} />
                <span className="partner-pool__name">
                  {item.playerName}
                  {item.note && <span className="partner-pool__note-inline">„{item.note}"</span>}
                </span>
                <RatingChip tag="D" value={item.doublesRating} />
                <span className="partner-pool__actions">
                  {sentOffer ? (
                    <>
                      <span className="partner-pool__status" data-testid={`partner-offer-status-${item.playerId}`}>
                        {outgoingOfferLabel(sentOffer.status)}
                      </span>
                      {sentOffer.status === 'PENDING' && (
                        <button
                          type="button"
                          className="btn-secondary"
                          disabled={busy}
                          data-testid={`partner-offer-withdraw-${sentOffer.id}`}
                          onClick={() =>
                            run(
                              `${mePath}/partner-offers/${sentOffer.id}${codeQuery}`,
                              { method: 'DELETE' },
                              'Propozycja wycofana.',
                            )
                          }
                        >
                          Wycofaj
                        </button>
                      )}
                    </>
                  ) : (
                    me?.canUsePool && (
                      <button
                        type="button"
                        className="btn-primary"
                        disabled={busy}
                        data-testid={`partner-offer-send-${item.playerId}`}
                        onClick={() =>
                          run(
                            `${mePath}/partner-search/${item.id}/offers`,
                            { method: 'POST', body: JSON.stringify({ code: accessCode ?? undefined }) },
                            `Propozycja wysłana do ${item.playerName} — czeka na jej akceptację.`,
                          )
                        }
                      >
                        Zagrajmy razem
                      </button>
                    )
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};
